import { useRef, useEffect } from "react";

interface UseInfiniteScrollMessagesProps {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  messages: any[];
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  threshold?: number;
}

export const useInfiniteScrollMessages = ({
  messagesContainerRef,
  messages,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  threshold = 200,
}: UseInfiniteScrollMessagesProps) => {
  const scrollHeightBeforeLoad = useRef<number>(0);
  const scrollTopBeforeLoad = useRef<number>(0);
  const previousMessagesCount = useRef(messages.length);

  // Preserve scroll position after loading older messages
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const currentMessagesCount = messages.length;

    if (
      currentMessagesCount > previousMessagesCount.current &&
      scrollHeightBeforeLoad.current > 0
    ) {
      const heightDifference =
        container.scrollHeight - scrollHeightBeforeLoad.current;

      requestAnimationFrame(() => {
        const newScrollTop = scrollTopBeforeLoad.current + heightDifference;
        const maxScrollTop = container.scrollHeight - container.clientHeight;
        container.scrollTop = Math.min(newScrollTop, maxScrollTop);
      });

      scrollHeightBeforeLoad.current = 0;
      scrollTopBeforeLoad.current = 0;
    }

    previousMessagesCount.current = currentMessagesCount;
  }, [messages.length, messagesContainerRef]);

  // Auto-load more messages when scrolling near the top
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isFetchingNextPage || !hasNextPage) {
        return;
      }

      if (container.scrollTop <= threshold) {
        scrollHeightBeforeLoad.current = container.scrollHeight;
        scrollTopBeforeLoad.current = container.scrollTop;
        fetchNextPage?.();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    threshold,
    messagesContainerRef,
  ]);
};
